export const MANDATORY_FEATURE_CATEGORIES = [
  "vehicle",
  "stop",
  "route",
  "admin",
  "settings",
];

export const BASE_FEATURE_CATEGORIES = [
  "vehicle",
  "stop",
  "route",
  "schedule",
  "site",
  "driver",
  "device",
  "tracking247",
  "alerts",
  "manageData",
];

export const SCHOOL_FEATURE_CATEGORIES = ["student", "attendant"];

export const FEATURE_CATEGORIES_WITHOUT_FEATURES = [
  "site",
  "driver",
  "device",
  "tracking247",
  "alerts",
  "student",
  "attendant",
  "manageData",
];

export const normalizeApiPath = (apiPath = "") => {
  const trimmedPath = apiPath.trim();

  if (!trimmedPath) {
    return "";
  }

  return trimmedPath.startsWith("/") ? trimmedPath : `/${trimmedPath}`;
};

export const getPredefinedFeatureCategories = ({
  includeSchoolCategories = false,
} = {}) =>
  includeSchoolCategories
    ? [...BASE_FEATURE_CATEGORIES, ...SCHOOL_FEATURE_CATEGORIES]
    : BASE_FEATURE_CATEGORIES;

export const getFeatureCategory = (feature) => {
  const apiPath =
    feature?.api_path || feature?.apiPath || feature?.APIPath || "";
  const normalizedPath = normalizeApiPath(apiPath);
  const rawParts = normalizedPath
    .split("/")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  const apiIndex = rawParts[0] === "api" ? 1 : 0;
  const basePath = rawParts[apiIndex] || "";
  const subPath = rawParts[apiIndex + 1] || "";

  if (basePath === "managedata") return "manageData";
  if (basePath === "employee" || basePath === "attendant") return "attendant";
  if (basePath === "driver" || basePath.startsWith("driver")) return "driver";
  if (basePath === "device" || basePath.startsWith("device")) return "device";
  if (basePath === "alert" || basePath.startsWith("alert")) return "alerts";
  if (basePath === "vehicle" && subPath === "alert") return "alerts";
  if (basePath.startsWith("user") || basePath.includes("role")) {
    return "admin";
  }
  if (basePath === "settings" || basePath === "label") return "settings";
  if (
    basePath === "vehicle" ||
    basePath.startsWith("vehicle") ||
    basePath.includes("speed") ||
    basePath.includes("incident")
  ) {
    return "vehicle";
  }
  if (
    basePath === "stop" ||
    basePath.startsWith("stop") ||
    basePath.includes("stop")
  ) {
    return "stop";
  }
  if (basePath === "route" || basePath.startsWith("route")) return "route";
  if (
    basePath === "schedule" ||
    basePath.startsWith("schedule") ||
    basePath === "trip" ||
    basePath.startsWith("trip") ||
    basePath.includes("trip")
  ) {
    return "schedule";
  }
  if (basePath === "student" || basePath.startsWith("student")) return "student";
  if (basePath === "site" || basePath.startsWith("site")) return "site";

  // Fallback by name / displayName / accessName
  const featureName = String(
    feature?.name ||
      feature?.displayName ||
      feature?.accessName ||
      feature?.access_name ||
      "",
  ).toLowerCase();

  if (featureName.includes("attendant") || featureName.includes("employee")) return "attendant";
  if (featureName.includes("driver")) return "driver";
  if (featureName.includes("device")) return "device";
  if (featureName.includes("vehicle") || featureName.includes("speed") || featureName.includes("incident") || featureName.includes("overspeed")) return "vehicle";
  if (featureName.includes("route")) return "route";
  if (featureName.includes("stop")) return "stop";
  if (featureName.includes("schedule") || featureName.includes("trip")) return "schedule";
  if (featureName.includes("student")) return "student";
  if (featureName.includes("site")) return "site";
  if (featureName.includes("user") || featureName.includes("role")) return "admin";

  if (basePath) return basePath;

  return null;
};

export const getFeaturesForCategory = (features, category) =>
  features.filter((feature) => getFeatureCategory(feature) === category);

export const getOrderedFeatureCategories = (
  features,
  {
    includeSchoolCategories = false,
    alwaysVisibleCategories = FEATURE_CATEGORIES_WITHOUT_FEATURES,
  } = {},
) => {
  const predefinedCategories = getPredefinedFeatureCategories({
    includeSchoolCategories,
  });

  const apiCategories = Array.from(
    new Set(features.map((feature) => getFeatureCategory(feature)).filter(Boolean)),
  ).filter(
    (category) =>
      predefinedCategories.includes(category) || category === "admin",
  );

  return [
    ...predefinedCategories.filter(
      (category) =>
        apiCategories.includes(category) ||
        alwaysVisibleCategories.includes(category),
    ),
    "admin",
  ];
};

export const getFeatureCategoryDisplayName = (category) => {
  if (category === "tracking247") return "24/7 Tracking";
  if (category === "alerts") return "Alerts & Notifications";
  if (category === "admin") return "Admin Panel";
  if (category === "manageData") return "Manage Data";
  if (category === "settings") return "Settings";
  if (category === "student") return "Student Management";
  if (category === "attendant") return "Attendant Management";

  return category.charAt(0).toUpperCase() + category.slice(1) + " Management";
};

export const normalizeOrganizationTypes = (organizationType) => {
  if (!organizationType) {
    return [];
  }

  const rawTypes = Array.isArray(organizationType)
    ? organizationType
    : [organizationType];

  return rawTypes
    .flatMap((type) =>
      typeof type === "string" ? type.split(",") : [type],
    )
    .map((type) => (typeof type === "string" ? type.trim() : type))
    .filter(Boolean);
};

export const includesSchoolTracking = (...organizationTypeSources) =>
  organizationTypeSources.some((organizationType) =>
    normalizeOrganizationTypes(organizationType).includes("schoolTrack"),
  );
